/**
 * Shared config tooltip generators and label maps for pricing configuration.
 * Used by Summary.tsx and PricingAgentActivity.tsx to avoid duplication.
 */

export interface ConfigEntry {
  label: string;
  value: string;
  tooltip: string;
}

const AGGRESSIVENESS_LABELS: Record<number, string> = {
  1: "Very Conservative",
  2: "Conservative",
  3: "Balanced",
  4: "Aggressive",
  5: "Very Aggressive",
};

const AGGRESSIVENESS_TOOLTIPS: Record<number, string> = {
  1: "Prices change minimally — only reacting to strong demand signals.",
  2: "Prices adjust cautiously, favoring stability over capturing upside.",
  3: "Balanced between stability and responsiveness. This is the default.",
  4: "Prices move quickly to capture demand shifts and revenue opportunities.",
  5: "Maximum responsiveness — prices swing aggressively with every demand signal.",
};

export function getAggressivenessTooltip(level: number): string {
  return AGGRESSIVENESS_TOOLTIPS[level] ?? "";
}

export function getAggressivenessLabel(level: number): string {
  return AGGRESSIVENESS_LABELS[level] ?? String(level);
}

export function getMarketInfluenceTooltip(influence: number): string {
  if (influence === 0) return "Competitor rates are completely ignored — pricing is based solely on your own demand.";
  const pct = Math.round(influence * 100);
  if (influence < 0.5) return `Competitor rates have minimal influence (${pct}%) — your pricing is mostly demand-driven.`;
  if (influence === 1) return "Competitor rates have full influence on your pricing. This is the default.";
  return `Competitor rates have significant influence (${pct}%) on your pricing.`;
}

export function getPositionOffsetTooltip(offset: number): string {
  if (offset === 0) return "No offset — pricing targets the same level as your competitive set. This is the default.";
  if (offset > 0) return `Targeting rates ${offset}% above your competitive set — positioning as a premium option.`;
  return `Targeting rates ${Math.abs(offset)}% below your competitive set — positioning as a value option.`;
}

export function getFadeWithUrgencyTooltip(enabled: boolean): string {
  return enabled
    ? "As the stay date approaches, competitor influence fades and your own demand signals take priority."
    : "Competitor influence stays constant regardless of how close the stay date is. This is the default.";
}

export function getCloseInAmplifierTooltip(enabled: boolean): string {
  return enabled
    ? "Price adjustments are amplified for dates arriving soon, reacting faster to last-minute demand."
    : "No amplification for close-in dates — adjustments are the same regardless of proximity. This is the default.";
}

export function getRateResistanceTooltip(factor: number): string {
  if (factor >= 0.8) return `High resistance (${factor}) — rates change very little between calculation cycles.`;
  if (factor >= 0.5) return `Moderate resistance (${factor}) — rates adjust steadily but avoid large swings.`;
  return `Low resistance (${factor}) — rates can shift substantially each cycle.`;
}

export function getResponsivenessTooltip(field: "events" | "differentials", value: number): string {
  if (field === "events") {
    if (value === 1) return "Standard event responsiveness — events have normal impact on pricing. This is the default.";
    return value > 1
      ? `Events have ${value}x amplified impact — prices rise more during high-attendance events.`
      : `Events have ${value}x dampened impact — prices react less to nearby events.`;
  }
  if (value === 1) return "Standard room type spread — differentials adjust at normal pace. This is the default.";
  return value > 1
    ? `Room type spreads widen ${value}x faster — premium rooms diverge more from base rates.`
    : `Room type spreads are dampened to ${value}x — all room types stay closer in price.`;
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatIsoDate(iso: string): { label: string; year: number } | null {
  const parts = iso.trim().split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  return { label: `${SHORT_MONTHS[m - 1]} ${d}`, year: y };
}

export function formatRuleDateRange(startDate?: string, endDate?: string): string {
  if (!startDate) return "";
  const s = formatIsoDate(startDate);
  if (!s) return startDate;

  if (!endDate) return `${s.label}, '${String(s.year).slice(2)}`;

  const e = formatIsoDate(endDate);
  if (!e) return `${s.label}, '${String(s.year).slice(2)}`;

  if (s.year === e.year) return `${s.label} – ${e.label}, '${String(s.year).slice(2)}`;
  return `${s.label}, '${String(s.year).slice(2)} – ${e.label}, '${String(e.year).slice(2)}`;
}

export function formatDateRangeString(dateRange: string): string {
  const separator = dateRange.includes("–") ? "–" : dateRange.includes(" - ") ? " - " : null;
  if (!separator) {
    const single = formatIsoDate(dateRange);
    return single ? `${single.label}, '${String(single.year).slice(2)}` : dateRange;
  }
  const [rawStart, rawEnd] = dateRange.split(separator);
  return formatRuleDateRange(rawStart.trim(), rawEnd.trim());
}

export function formatIsoDatesInText(text: string): string {
  return text
    .replace(/(\d{4}-\d{2}-\d{2})\s*[–\-]\s*(\d{4}-\d{2}-\d{2})/g, (_, a, b) =>
      formatRuleDateRange(a, b))
    .replace(/\d{4}-\d{2}-\d{2}/g, (match) => {
      const parsed = formatIsoDate(match);
      return parsed ? `${parsed.label}, '${String(parsed.year).slice(2)}` : match;
    });
}

/**
 * Build a list of non-default config entries for display in summary views.
 */
const BOOLEAN_ACTION_KEYS = new Set([
  "competitive.closeInAmplifier",
  "competitive.fadeWithUrgency",
  "competitive.followCompetitors",
]);

export function formatActionValue(key: string, value: any): string {
  if (BOOLEAN_ACTION_KEYS.has(key)) return value ? "On" : "Off";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (typeof value === "number") return `${value}%`;
  return String(value);
}

const CONDITION_METRIC_LABELS: Record<string, string> = {
  forecastedOccupancy: "Forecasted Occupancy",
  avgDailyRate: "Average Daily Rate",
  revPar: "RevPAR",
  competitorRate: "Competitor Rate",
};

const PERCENTAGE_CONDITION_METRICS = new Set([
  "forecastedOccupancy",
]);

export function formatConditionDisplay(condition: { metric: string; operator: string; value: any }): string {
  const label = CONDITION_METRIC_LABELS[condition.metric]
    ?? condition.metric.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
  const suffix = PERCENTAGE_CONDITION_METRICS.has(condition.metric) ? "%" : "";
  return `${label} ${condition.operator} ${condition.value}${suffix}`;
}

export function getNonDefaultConfigEntries(config: any): ConfigEntry[] {
  if (!config) return [];
  const entries: ConfigEntry[] = [];

  if (config.aggressiveness != null && config.aggressiveness !== 3) {
    entries.push({
      label: "Aggressiveness",
      value: getAggressivenessLabel(config.aggressiveness),
      tooltip: getAggressivenessTooltip(config.aggressiveness),
    });
  }
  if (config.competitive?.influence != null && config.competitive.influence !== 1) {
    entries.push({
      label: "Market Influence",
      value: `${Math.round(config.competitive.influence * 100)}%`,
      tooltip: getMarketInfluenceTooltip(config.competitive.influence),
    });
  }
  if (config.competitive?.positionOffset != null && config.competitive.positionOffset !== 0) {
    entries.push({
      label: "Position Offset",
      value: `${config.competitive.positionOffset}%`,
      tooltip: getPositionOffsetTooltip(config.competitive.positionOffset),
    });
  }
  if (config.competitive?.fadeWithUrgency === true) {
    entries.push({
      label: "Fade competitor effect near arrival",
      value: "On",
      tooltip: getFadeWithUrgencyTooltip(true),
    });
  }
  if (config.competitive?.closeInAmplifier === true) {
    entries.push({
      label: "Price to fill near arrival",
      value: "On",
      tooltip: getCloseInAmplifierTooltip(true),
    });
  }
  if (config.resistance?.factor != null) {
    entries.push({
      label: "Rate Resistance",
      value: String(config.resistance.factor),
      tooltip: getRateResistanceTooltip(config.resistance.factor),
    });
  }
  if (config.events?.responsiveness != null && config.events.responsiveness !== 1) {
    entries.push({
      label: "Event Responsiveness",
      value: `${config.events.responsiveness}x`,
      tooltip: getResponsivenessTooltip("events", config.events.responsiveness),
    });
  }
  if (config.differentials?.responsiveness != null && config.differentials.responsiveness !== 1) {
    entries.push({
      label: "Room Type Spread",
      value: `${config.differentials.responsiveness}x`,
      tooltip: getResponsivenessTooltip("differentials", config.differentials.responsiveness),
    });
  }
  return entries;
}
